export type ShirtColor = 'czarna' | 'biała';
export const SHIRT_COLORS: readonly ShirtColor[] = ['czarna', 'biała'];
export type ShirtCut = 'męski' | 'damski';
export type Size = 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL' | '3XL' | '4XL' | '5XL';
export type ShippingMethod = 'paczkomat' | 'kurier' | 'odbior' | 'zagranica';
// Mirrors the `orders_status_check` constraint in apps/api/src/db/schema.ts.
export type OrderStatus =
  | 'awaiting_payment'
  | 'paid'
  | 'shipped'
  | 'merged'
  | 'pickup'
  | 'cancelled';

export type CommentTopic = 'combine' | 'addto' | 'other' | 'none';

export type OrderItem = {
  color: ShirtColor;
  cut: ShirtCut;
  size: Size;
  qty: number;
  unitPrice: number;
};

export type ShippingAddress = {
  method: ShippingMethod;
  paczkomat?: { code: string; address: string; note?: string };
  courier?: { street: string; postalCode: string; city: string };
  international?: { country: string; countryName: string };
  pickupNote?: string;
};

export type Order = {
  id: string;
  customerName: string;
  email: string;
  phone: string;
  externalOrderId: string | null;
  items: OrderItem[];
  shipping: ShippingAddress;
  status: OrderStatus;
  createdAt: string;
  trackingNumber?: string;
  customerComment?: { topic: Exclude<CommentTopic, 'none'>; text: string };
  internalNote?: string;
  flag?: 'manual' | 'merged-into' | 'merged-with' | 'comment';
  flagRef?: string;
  total: number;
  packageSize?: 'A' | 'B' | 'C';
};

export type ExternalOrderHolder = {
  firstName: string;
};

export type ShippingMode = 'choose' | 'ride_along' | 'closed';

/** One colour's share of an external order's shirt allowance. */
export type ColorAllowance = {
  allowance: number;
  alreadyOrdered: number;
  remaining: number;
};

export type ValidateExternalOrderResult =
  | {
      valid: true;
      holder: ExternalOrderHolder;
      externalOrderId: string;
      ticketCount: number;
      allowance: number;
      alreadyOrdered: number;
      remaining: number;
      /**
       * Each ticket buys up to the per-colour quota of every colour, so a second shirt on one
       * ticket has to be the other colour. `remaining` above is already capped by these.
       */
      byColor: Record<ShirtColor, ColorAllowance>;
      /**
       * What the delivery step should do. `ride_along` means an earlier order for this external
       * order still has an unshipped parcel, so this one joins it for free; `closed` means every
       * earlier parcel has gone, and the shop takes no second order.
       */
      shippingMode: ShippingMode;
    }
  | { valid: false };

export type DashboardPeriod = 'day' | 'week' | 'month';

export type DashboardPoint = { at: string; count: number };

export type DashboardData = {
  totals: {
    orders: number;
    ordersDelta: number;
    shirts: number;
    shirtsDelta: number;
    toShip: number;
    toShipOver48h: number;
    cancelled: number;
    cancelledRate: number;
  };
  period: DashboardPeriod;
  periodHeading: string;
  seriesUnit: 'hour' | 'day';
  series: DashboardPoint[];
  concertDate: string | null;
  daysToConcert: number | null;
  countdown: string | null;
  sizes: { size: Size; count: number; pct: number }[];
  colorCut: { name: string; count: number; pct: number }[];
  shipping: {
    paczkomat: { count: number; pct: number };
    kurier: { count: number; pct: number };
    odbior: { count: number; pct: number };
    zagranica: { count: number; pct: number };
  };
};

export const SHIPPING_LABEL: Record<ShippingMethod, string> = {
  paczkomat: 'Paczkomat InPost',
  kurier: 'Kurier InPost',
  odbior: 'Odbiór osobisty',
  zagranica: 'Wysyłka zagraniczna',
};

// Legacy formatter — accepts złotówki (decimal). New code should use util/money.ts.
export const formatZl = (n: number) =>
  n.toFixed(2).replace('.', ',') + ' zł';
